import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { ApiError, NetworkError } from '@/api/client';
import { LoadingState, EmptyState } from '@/components/EmptyState';
import { OnboardingNotice } from '@/components/OnboardingNotice';
import { SkuInfo, skuDisplay } from '@/components/SkuInfo';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatNumber } from '@/i18n/format';
import type { FilePreview, RequestKind, RequestSku, ShipToInput } from '@/api/types';
import {
  MAX_FILE_BYTES, XLSX_MIME, describeRequestError, describeRow, downloadBase64, fileToBase64, newRequestKey,
} from '@/lib/requests';
import { requestBase } from '@/lib/drafts';
import { describeConflict, revisionConflicts } from '@/lib/revisions';

/**
 * Seller self-service request - one screen for both kinds:
 *   INBOUND  -> an expected supply (nothing is added to stock until the warehouse receives it)
 *   OUTBOUND -> an order; the goods are reserved the moment it is sent to the warehouse
 * A request is always saved as a private DRAFT first. "Save as draft" keeps it for later; "Send to work" saves it,
 * asks for confirmation (a sent request can no longer be deleted - only cancelled) and sends it. If sending fails
 * (e.g. not enough stock) the draft stays saved. The same screen edits an existing draft (`draft` seed).
 * Lines come from a search of the seller's OWN catalogue or from an Excel file;
 * either way they end in the same review step and the same create call, with
 * one idempotency key per submission (kept in sessionStorage until success),
 * so a double click or a lost connection never makes two documents.
 */
type Line = {
  skuId: string; code: string; name: string; qty: string; available: number | null;
  article?: string; size?: string; color?: string; barcodes?: readonly string[];
};
type Step = 'edit' | 'review' | 'done';
type Done = { id: string; number: string; href: string };

const KEY_STORE = (kind: RequestKind) => `portal-request-key-${kind}`;
function storedKey(kind: RequestKind): string {
  try {
    const k = sessionStorage.getItem(KEY_STORE(kind));
    if (k) return k;
    const fresh = newRequestKey();
    sessionStorage.setItem(KEY_STORE(kind), fresh);
    return fresh;
  } catch {
    return newRequestKey();
  }
}
function clearKey(kind: RequestKind): void {
  try {
    sessionStorage.removeItem(KEY_STORE(kind));
  } catch {
    /* storage unavailable - nothing to clear */
  }
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export type DraftSeed = {
  readonly id: string;
  readonly ownerId: string;
  readonly warehouseId: string;
  readonly reference: string;
  readonly date: string;
  /** How the order is fulfilled; '' = not chosen yet (the seller must choose before sending an outbound order). */
  readonly fulfilment?: '' | 'STANDARD' | 'FBS';
  readonly shipTo: ShipToInput;
  readonly lines: readonly { skuId: string; code: string; name: string; qty: string; article?: string; size?: string; color?: string; barcodes?: readonly string[] }[];
};

/** Editing a request that is already at the warehouse (ADR-089): `mode` HEADER = the items are locked (picking started). */
export type ReviseSeed = { readonly revisionNo: number; readonly mode: 'FULL' | 'HEADER' };

const WHOLE = /^[1-9]\d{0,8}$/;
const EMPTY_SHIP: ShipToInput = { name: '', phone: '', address: '', city: '', postalCode: '', country: '', comment: '' };

export function RequestForm({ kind, draft, revise }: { kind: RequestKind; draft?: DraftSeed; revise?: ReviseSeed }): JSX.Element {
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const out = kind === 'OUTBOUND';
  const options = useApi(() => endpoints.requestOptions(), []);
  const [ownerId, setOwnerId] = useState(draft?.ownerId ?? '');
  const [warehouseId, setWarehouseId] = useState(draft?.warehouseId ?? '');
  const [draftId, setDraftId] = useState<string | null>(draft?.id ?? null);
  const [savedHref, setSavedHref] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>(() => (draft?.lines ?? []).map((l) => ({ ...l, available: null })));
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 250);
  const [reference, setReference] = useState(draft?.reference ?? '');
  const [date, setDate] = useState(draft?.date ?? '');
  const [shipTo, setShipTo] = useState<ShipToInput>(draft?.shipTo ?? EMPTY_SHIP);
  // No default on purpose: STANDARD vs FBS decides how the warehouse handles the order, so the seller chooses explicitly.
  const [fulfilment, setFulfilment] = useState<'' | 'STANDARD' | 'FBS'>(draft?.fulfilment ?? '');
  const [step, setStep] = useState<Step>('edit');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [preview, setPreview] = useState<FilePreview | null>(null);
  const [fileBusy, setFileBusy] = useState(false);
  const [fileError, setFileError] = useState<unknown>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const keyRef = useRef<string>(storedKey(kind));
  const reviseKeyRef = useRef<string>(newRequestKey());
  const locked = revise?.mode === 'HEADER';

  const owners = options.status === 'success' ? options.data.owners : [];
  const owner = owners.find((o) => o.id === ownerId) ?? (owners.length === 1 ? owners[0] : undefined);
  const warehouses = owner?.warehouses ?? [];

  // Auto-pick when there is only one choice - fewer clicks, nothing hidden.
  useEffect(() => {
    if (owners.length === 1 && ownerId === '') setOwnerId(owners[0]!.id);
  }, [owners, ownerId]);
  useEffect(() => {
    if (warehouses.length === 1 && warehouseId === '') setWarehouseId(warehouses[0]!.id);
  }, [warehouses, warehouseId]);

  const ready = Boolean(owner && warehouseId);
  const skus = useApi(
    () => (ready ? endpoints.requestSkus({ ownerId: owner!.id, warehouseId, ...(q ? { q } : {}) }) : Promise.resolve({ rows: [] as RequestSku[] })),
    [ready, owner?.id, warehouseId, q],
  );

  function resetLines(): void {
    setLines([]);
    setPreview(null);
    setNotice(null);
  }

  function addSku(s: RequestSku): void {
    setNotice(null);
    setLines((prev) => {
      const i = prev.findIndex((l) => l.skuId === s.sku_id);
      if (i >= 0) {
        setNotice(t('requests.lineMerged', { sku: s.code }));
        return prev.map((l, j) => (j === i ? { ...l, qty: String((Number(l.qty) || 0) + 1) } : l));
      }
      return [...prev, {
        skuId: s.sku_id, code: s.code, name: s.name, qty: '1', available: out ? Number(s.available) : null,
        ...skuDisplay(s), ...(!s.barcodes && s.barcode ? { barcodes: [s.barcode] } : {}),
      }];
    });
  }

  const problems = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of lines) {
      if (!WHOLE.test(l.qty.trim())) m.set(l.skuId, t('requests.qtyInvalid'));
      else if (out && l.available !== null && Number(l.qty) > l.available) {
        m.set(l.skuId, t('requests.err.insufficient', { sku: l.code, available: formatNumber(String(l.available), locale), requested: l.qty }));
      }
    }
    return m;
  }, [lines, out, t, locale]);

  const shipOk = !out || (shipTo.name.trim() && shipTo.address.trim() && shipTo.city.trim() && shipTo.country.trim());
  // a request already at the warehouse is revised without touching its fulfilment type (the field is not shown then)
  const modelOk = !out || Boolean(revise) || fulfilment !== '';
  const canReview = ready && lines.length > 0 && problems.size === 0 && Boolean(shipOk) && modelOk;
  // A draft only needs a warehouse and whole-number quantities; stock and the recipient are checked when it is sent.
  const canSaveDraft = ready && lines.length > 0 && lines.every((l) => WHOLE.test(l.qty.trim()));
  const totalUnits = lines.reduce((sum, l) => sum + (Number(l.qty) || 0), 0);

  async function onTemplate(): Promise<void> {
    try {
      const r = await endpoints.requestTemplate(kind, locale);
      downloadBase64(r.filename, r.bytesBase64, XLSX_MIME);
    } catch (e) {
      setFileError(e);
    }
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !owner) return;
    setPreview(null);
    setFileError(null);
    if (file.size > MAX_FILE_BYTES) {
      setFileError(new ApiError(400, { code: 'SELLER_REQUEST.FILE_INVALID', params: { reason: 'TOO_LARGE' }, request_id: '-' }));
      return;
    }
    setFileBusy(true);
    try {
      const fileBase64 = await fileToBase64(file);
      setPreview(await endpoints.previewRequestFile({ kind, ownerId: owner.id, warehouseId, fileBase64 }));
    } catch (err) {
      setFileError(err);
    } finally {
      setFileBusy(false);
    }
  }

  function applyPreview(): void {
    if (!preview || preview.errorCount > 0) return;
    setLines(preview.lines.map((l) => ({ skuId: l.skuId, code: l.code, name: l.name, qty: l.qty, available: out ? Number(l.available ?? '0') : null })));
    setPreview(null);
    setNotice(t('requests.fileApplied', { n: preview.lines.length }));
  }

  /** The request body shared by create and update; the recipient is sent as far as it is filled in. */
  function bodyFields() {
    const dueAt = date ? new Date(`${date}T${out ? '23:59:00' : '12:00:00'}`).toISOString() : undefined;
    const ship: Partial<ShipToInput> = {};
    for (const k of ['name', 'phone', 'address', 'city', 'postalCode', 'country', 'comment'] as const) if (shipTo[k]?.trim()) ship[k] = shipTo[k]!.trim();
    return {
      lines: lines.map((l) => ({ skuId: l.skuId, qty: l.qty.trim() })),
      ...(reference.trim() ? { reference: reference.trim() } : {}),
      dueAt,
      ship,
      ...(out && fulfilment ? { fulfilmentModel: fulfilment } : {}),
    };
  }

  /**
   * Saves what is on screen as the DRAFT (creates it the first time - one idempotency key per attempt, kept until it
   * succeeds - and updates it afterwards) and returns its id. Nothing is sent to the warehouse here.
   */
  async function persistDraft(): Promise<string> {
    if (!owner) throw new Error('no company');
    const f = bodyFields();
    if (draftId) {
      if (out) await endpoints.updateOutboundDraft(draftId, { warehouseId, lines: f.lines, ...(f.reference ? { reference: f.reference } : {}), ...(f.dueAt ? { dueAt: f.dueAt } : {}), ...(f.fulfilmentModel ? { fulfilmentModel: f.fulfilmentModel } : {}), shipTo: f.ship });
      else await endpoints.updateInboundDraft(draftId, { warehouseId, lines: f.lines, ...(f.reference ? { reference: f.reference } : {}), ...(f.dueAt ? { expectedAt: f.dueAt } : {}) });
      return draftId;
    }
    const idempotencyKey = keyRef.current;
    let id: string;
    try {
      const common = { idempotencyKey, ownerId: owner.id, warehouseId, lines: f.lines, ...(f.reference ? { reference: f.reference } : {}) };
      id = out
        ? (await endpoints.createOutboundDraft({ ...common, ...(f.dueAt ? { dueAt: f.dueAt } : {}), ...(f.fulfilmentModel ? { fulfilmentModel: f.fulfilmentModel } : {}), shipTo: f.ship })).id
        : (await endpoints.createInboundDraft({ ...common, ...(f.dueAt ? { expectedAt: f.dueAt } : {}) })).id;
    } catch (err) {
      if (!(err instanceof NetworkError)) throw err;
      // The request may or may not have reached the server: ask by our own key before offering a retry.
      const found = (await endpoints.requestByKey(idempotencyKey).catch(() => ({ request: null }))).request;
      if (!found) throw err;
      id = found.id;
    }
    clearKey(kind);
    keyRef.current = newRequestKey();
    setDraftId(id);
    return id;
  }

  async function saveDraft(): Promise<void> {
    setSubmitting(true);
    setError(null);
    setSavedHref(null);
    try {
      const id = await persistDraft();
      navigate(`${requestBase(kind)}/${id}`);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  async function send(): Promise<void> {
    if (!owner) return;
    if (!window.confirm(t(out ? 'drafts.out.submitConfirm' : 'drafts.in.submitConfirm'))) return;
    setSubmitting(true);
    setError(null);
    setSavedHref(null);
    let id: string | null = null;
    try {
      id = await persistDraft();
      const r = out ? await endpoints.submitOutboundDraft(id) : await endpoints.submitInboundDraft(id);
      finish({ id, number: r.number, href: `${requestBase(kind)}/${id}` });
    } catch (err) {
      // Nothing is lost: the draft stays saved and the same button works again.
      if (id) setSavedHref(`${requestBase(kind)}/${id}`);
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Revise mode: saves the edit of a request that is already at the warehouse. Only what the seller changed is sent
   * (dates/recipient/reference when edited; the full desired list of lines unless the items are locked); the server diffs
   * it against the current request, applies it as a whole or refuses it as a whole. The key is kept across a lost
   * connection and replaced after any answer, so a retry is safe and a corrected edit is never mistaken for a replay.
   */
  async function saveRevision(): Promise<void> {
    if (!revise || !draft) return;
    if (!window.confirm(t('revise.confirm'))) return;
    setSubmitting(true);
    setError(null);
    try {
      const f = bodyFields();
      const dirtyRef = reference.trim() !== (draft.reference ?? '').trim();
      const dirtyDate = date !== draft.date;
      const dirtyShip = out && (['name', 'phone', 'address', 'city', 'postalCode', 'country', 'comment'] as const).some((k) => (shipTo[k] ?? '').trim() !== (draft.shipTo[k] ?? '').trim());
      const common = {
        idempotencyKey: reviseKeyRef.current,
        expectedRevision: revise.revisionNo,
        ...(dirtyRef ? { reference: reference.trim() || null } : {}),
        ...(locked ? {} : { lines: f.lines }),
      };
      const r = out
        ? await endpoints.reviseOutboundRequest(draft.id, { ...common, ...(dirtyDate ? { dueAt: f.dueAt ?? null } : {}), ...(dirtyShip ? { shipTo: f.ship as ShipToInput } : {}) })
        : await endpoints.reviseInboundRequest(draft.id, { ...common, ...(dirtyDate ? { expectedAt: f.dueAt ?? null } : {}) });
      reviseKeyRef.current = newRequestKey();
      if (!r.changed) {
        setNotice(t('revise.noChanges'));
        setStep('edit');
        return;
      }
      setDone({ id: draft.id, number: r.number, href: `${requestBase(kind)}/${draft.id}` });
      setStep('done');
    } catch (err) {
      if (!(err instanceof NetworkError)) reviseKeyRef.current = newRequestKey();
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  function finish(d: Done): void {
    clearKey(kind);
    keyRef.current = newRequestKey();
    setDone(d);
    setStep('done');
  }

  if (options.status === 'loading') return <LoadingState />;
  if (options.status === 'error') return <ErrorBanner error={options.error} onRetry={options.reload} />;

  const title = revise ? (out ? t('revise.title.out') : t('revise.title.in')) : draft ? (out ? t('requests.out.editTitle') : t('requests.in.editTitle')) : out ? t('requests.out.title') : t('requests.in.title');

  if (step === 'done' && done) {
    return (
      <div className="request-form">
        <div className="page-header">
          <h1>{title}</h1>
        </div>
        <div className="card" role="status">
          <p className="request-done">{revise ? t('revise.done', { number: done.number }) : out ? t('requests.out.done', { number: done.number }) : t('requests.in.done', { number: done.number })}</p>
          <p className="sub">{revise ? t('revise.doneHint') : out ? t('requests.out.doneHint') : t('requests.in.doneHint')}</p>
          <div className="form-actions">
            <Link className="button-link primary" to={done.href}>
              {revise ? t('revise.backToRequest') : t('requests.openCreated')}
            </Link>
            {!revise && <button
              type="button"
              onClick={() => {
                setDone(null);
                setDraftId(null);
                setSavedHref(null);
                setLines([]);
                setReference('');
                setDate('');
                setShipTo(EMPTY_SHIP);
                setStep('edit');
              }}
            >
              {t('requests.createAnother')}
            </button>}
          </div>
        </div>
      </div>
    );
  }

  if (owners.length === 0 || (owner && warehouses.length === 0)) {
    // Say WHY in words (no warehouse assigned / contract not active) - the server decided it, never a technical message.
    const why = owner?.onboarding?.reason;
    return (
      <div className="request-form">
        <div className="page-header">
          <h1>{title}</h1>
        </div>
        {why && why !== 'READY' ? <OnboardingNotice reason={why} /> : <EmptyState title={t('onboarding.NO_WAREHOUSE')} hint={t('onboarding.nextStep')} />}
      </div>
    );
  }

  const errText = error ? describeRequestError(t, error) : null;
  /** A refused revision explains itself per line (nothing was changed); a stale one points to the current version. */
  const renderErr = (): JSX.Element => {
    const conflicts = revisionConflicts(error);
    if (conflicts && conflicts.length > 0) {
      return (
        <div className="error-banner" role="alert" data-testid="revise-conflict">
          <strong>{t('revise.conflictTitle')}</strong>
          <ul className="conflict-list">
            {conflicts.map((c) => <li key={`${c.skuId}-${c.reason}`}>{describeConflict(t, c)}</li>)}
          </ul>
        </div>
      );
    }
    if (error instanceof ApiError && error.code === 'SELLER_REQUEST.REVISION_STALE' && draft) {
      return <div className="error-banner" role="alert" data-testid="revise-stale">{t('revise.stale')} <Link to={`${requestBase(kind)}/${draft.id}`}>{t('revise.openCurrent')}</Link></div>;
    }
    return errText ? <div className="error-banner" role="alert">{errText}</div> : <ErrorBanner error={error} />;
  };

  if (step === 'review') {
    const wh = warehouses.find((w) => w.id === warehouseId);
    return (
      <div className="request-form">
        <div className="page-header">
          <h1>{title}</h1>
        </div>
        <div className="card">
          <h2 className="section-title">{t('requests.reviewTitle')}</h2>
          <dl className="kv">
            {owners.length > 1 && (
              <>
                <dt>{t('requests.company')}</dt>
                <dd>{owner?.name}</dd>
              </>
            )}
            <dt>{t('requests.warehouse')}</dt>
            <dd>{wh?.name}</dd>
            <dt>{t('requests.items')}</dt>
            <dd>{t('requests.itemsSummary', { lines: lines.length, units: formatNumber(String(totalUnits), locale) })}</dd>
            {reference.trim() && (
              <>
                <dt>{out ? t('requests.out.reference') : t('requests.in.reference')}</dt>
                <dd>{reference.trim()}</dd>
              </>
            )}
            {out && fulfilment && (
              <>
                <dt>{t('requests.out.fulfilment')}</dt>
                <dd>{t(fulfilment === 'FBS' ? 'requests.out.fulfilmentFbs' : 'requests.out.fulfilmentStandard')}</dd>
              </>
            )}
            {out && (
              <>
                <dt>{t('requests.out.recipient')}</dt>
                <dd>{[shipTo.name, shipTo.address, shipTo.postalCode, shipTo.city, shipTo.country].filter((x) => x && x.trim()).join(', ')}</dd>
              </>
            )}
          </dl>
          <ul className="review-lines">
            {lines.map((l) => (
              <li key={l.skuId}>
                <span className="sku-main">
                  <SkuInfo sku={l} />
                </span>
                <span className="qty">{formatNumber(l.qty, locale)}</span>
              </li>
            ))}
          </ul>
          <div className="what-happens" role="note">
            {revise ? (locked ? t('revise.hint.header') : out ? t('revise.hint.out') : t('revise.hint.in')) : <><strong>{t('requests.whatHappens')}</strong> {out ? t('requests.out.whatHappens') : t('requests.in.whatHappens')}</>}
          </div>
          {error !== null && renderErr()}
          {savedHref && (
            <p className="notice" role="status">
              {t('drafts.savedKept')} <Link to={savedHref}>{t('drafts.openDraft')}</Link>
            </p>
          )}
          <div className="form-actions">
            {revise ? (
              <button type="button" className="primary" disabled={submitting} onClick={() => void saveRevision()}>
                {submitting ? t('revise.saving') : t('revise.save')}
              </button>
            ) : (
              <>
                <button type="button" className="primary" disabled={submitting} onClick={() => void send()}>
                  {submitting ? t('requests.sending') : out ? t('requests.out.submit') : t('requests.in.submit')}
                </button>
                <button type="button" disabled={submitting || !canSaveDraft} onClick={() => void saveDraft()}>
                  {t('requests.saveDraft')}
                </button>
              </>
            )}
            <button type="button" disabled={submitting} onClick={() => setStep('edit')}>
              {t('requests.backToEdit')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const onReview = (e: FormEvent) => {
    e.preventDefault();
    if (canReview) {
      setError(null);
      setStep('review');
    }
  };

  return (
    <form className="request-form" onSubmit={onReview} noValidate>
      <div className="page-header">
        <div>
          <h1>{title}</h1>
          <div className="sub">{out ? t('requests.out.subtitle') : t('requests.in.subtitle')}</div>
        </div>
      </div>

      <div className="card">
        {owners.length > 1 && !revise && (
          <div className="field-row">
            <label htmlFor="rq-owner">{t('requests.company')}</label>
            <select id="rq-owner" value={owner?.id ?? ''} onChange={(e) => { setOwnerId(e.target.value); setWarehouseId(''); resetLines(); }}>
              <option value="">{t('requests.chooseCompany')}</option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}
        {revise ? (
          // the warehouse of a request that is already at the warehouse never changes
          <div className="field-row" data-testid="fixed-warehouse">
            <span className="field-label">{t('requests.warehouseSingle')}</span>
            <div>{warehouses.find((w) => w.id === warehouseId)?.name ?? ''}</div>
          </div>
        ) : warehouses.length === 1 ? (
          // exactly one warehouse serves this client: it is simply used (no selector to get wrong)
          <div className="field-row" data-testid="single-warehouse">
            <span className="field-label">{t('requests.warehouseSingle')}</span>
            <div>{warehouses[0]!.name}</div>
          </div>
        ) : (
          <div className="field-row">
            <label htmlFor="rq-warehouse">{t('requests.warehouse')}</label>
            <select id="rq-warehouse" value={warehouseId} disabled={!owner} onChange={(e) => { setWarehouseId(e.target.value); resetLines(); }}>
              <option value="">{t('requests.chooseWarehouse')}</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
            {lines.length > 0 && <div className="disabled-note">{t('requests.changeResets')}</div>}
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="section-title">{t('requests.items')}</h2>
        {locked ? (
          <p className="sub">{t('revise.hint.header')}</p>
        ) : !ready ? (
          <p className="sub">{t('requests.chooseWarehouseFirst')}</p>
        ) : (
          <>
            <div className="field-row">
              <label htmlFor="rq-search">{t('requests.findItem')}</label>
              <input id="rq-search" type="search" autoComplete="off" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            {skus.status === 'success' && skus.data.rows.length === 0 && <p className="sub">{t('requests.noItemsFound')}</p>}
            {skus.status === 'success' && skus.data.rows.length > 0 && (
              <ul className="sku-results" aria-label={t('requests.itemResults')}>
                {skus.data.rows.map((s) => (
                  <li key={s.sku_id}>
                    <div className="sku-main">
                      <SkuInfo sku={s} />
                      {out && <div className="meta">{t('requests.available', { qty: formatNumber(s.available, locale) })}</div>}
                    </div>
                    <button type="button" onClick={() => addSku(s)} disabled={out && Number(s.available) <= 0} aria-label={t('requests.addItem', { sku: s.code })}>
                      {t('requests.add')}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="excel-box">
              <div className="excel-title">{t('requests.excelTitle')}</div>
              <p className="sub">{t('requests.excelHint')}</p>
              <div className="form-actions">
                <button type="button" onClick={() => void onTemplate()}>
                  {out ? t('requests.out.template') : t('requests.in.template')}
                </button>
                <button type="button" disabled={fileBusy} onClick={() => fileInput.current?.click()}>
                  {fileBusy ? t('requests.checkingFile') : t('requests.uploadExcel')}
                </button>
                <input ref={fileInput} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={(e) => void onFile(e)} aria-label={t('requests.uploadExcel')} />
              </div>
              {fileError !== null && (
                <div className="error-banner" role="alert">{describeRequestError(t, fileError) ?? t('requests.file.corrupt')}</div>
              )}
              {preview && (
                <div className="file-preview" aria-label={t('requests.previewTitle')}>
                  <div className={preview.errorCount > 0 ? 'error-banner' : 'notice'} role="status">
                    {preview.errorCount > 0
                      ? t('requests.previewErrors', { errors: preview.errorCount, rows: preview.dataRowCount })
                      : t('requests.previewOk', { lines: preview.lines.length, rows: preview.dataRowCount })}
                  </div>
                  <ul className="preview-rows">
                    {preview.rows.map((r) => (
                      <li key={r.rowNumber} className={`pr-${r.status.toLowerCase()}`}>
                        <span className="pr-row">{t('requests.rowN', { n: r.rowNumber })}</span>
                        <span className="pr-item">{r.skuCode ?? (r.code || r.barcode || '—')}{r.qty ? ` × ${r.qty}` : ''}</span>
                        <span className="pr-msg">{describeRow(t, r)}</span>
                      </li>
                    ))}
                  </ul>
                  {preview.errorCount === 0 && (
                    <div className="form-actions">
                      <button type="button" className="primary" onClick={applyPreview}>
                        {lines.length > 0 ? t('requests.replaceWithFile', { n: preview.lines.length }) : t('requests.useFile', { n: preview.lines.length })}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2 className="section-title">{t('requests.lines')}</h2>
        {notice && <div className="notice" role="status">{notice}</div>}
        {lines.length === 0 ? (
          <p className="sub">{t('requests.noLines')}</p>
        ) : (
          <ul className="request-lines">
            {lines.map((l) => {
              const problem = problems.get(l.skuId) ?? null;
              const errId = `rq-err-${l.skuId}`;
              return (
                <li key={l.skuId}>
                  <div className="rl-item">
                    <SkuInfo sku={l} />
                    {out && l.available !== null && <div className="meta">{t('requests.available', { qty: formatNumber(String(l.available), locale) })}</div>}
                  </div>
                  <div className="rl-controls">
                    <input
                      className="qty-input"
                      inputMode="numeric"
                      aria-label={t('requests.qtyFor', { sku: l.code })}
                      aria-invalid={problem ? 'true' : 'false'}
                      aria-describedby={problem ? errId : undefined}
                      value={l.qty}
                      disabled={locked}
                      onChange={(e) => setLines((prev) => prev.map((x) => (x.skuId === l.skuId ? { ...x, qty: e.target.value } : x)))}
                    />
                    <button type="button" className="danger" disabled={locked} aria-label={t('requests.removeLine', { sku: l.code })} onClick={() => setLines((prev) => prev.filter((x) => x.skuId !== l.skuId))}>
                      {t('requests.remove')}
                    </button>
                  </div>
                  {problem && <div className="field-error" id={errId}>{problem}</div>}
                </li>
              );
            })}
          </ul>
        )}
        {lines.length > 0 && <div className="lines-total">{t('requests.itemsSummary', { lines: lines.length, units: formatNumber(String(totalUnits), locale) })}</div>}
      </div>

      <div className="card">
        <h2 className="section-title">{out ? t('requests.out.details') : t('requests.in.details')}</h2>
        <div className="form-grid">
          <div className="field-row">
            <label htmlFor="rq-ref">{out ? t('requests.out.reference') : t('requests.in.reference')}</label>
            <input id="rq-ref" maxLength={40} value={reference} onChange={(e) => setReference(e.target.value)} />
            <div className="disabled-note">{t('requests.referenceHint')}</div>
          </div>
          <div className="field-row">
            <label htmlFor="rq-date">{out ? t('requests.out.date') : t('requests.in.date')}</label>
            <input id="rq-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
        {out && !revise && (
          <fieldset className="field-row" aria-required="true" aria-describedby="rq-fulfilment-hint" style={{ border: 0, padding: 0, margin: '12px 0' }}>
            <legend>{t('requests.out.fulfilment')} *</legend>
            {(['STANDARD', 'FBS'] as const).map((m) => (
              <label key={m} htmlFor={`rq-fulfilment-${m}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '6px 0' }}>
                <input id={`rq-fulfilment-${m}`} type="radio" name="rq-fulfilment" value={m} checked={fulfilment === m} onChange={() => setFulfilment(m)} style={{ width: 'auto', flex: 'none', margin: '3px 0 0' }} />
                <span>
                  <strong>{t(m === 'FBS' ? 'requests.out.fulfilmentFbs' : 'requests.out.fulfilmentStandard')}</strong>
                  <span className="disabled-note" style={{ display: 'block' }}>{t(m === 'FBS' ? 'requests.out.fulfilmentFbsHint' : 'requests.out.fulfilmentStandardHint')}</span>
                </span>
              </label>
            ))}
            {fulfilment === '' && <div className="disabled-note" id="rq-fulfilment-hint">{t('requests.out.fulfilmentRequired')}</div>}
          </fieldset>
        )}
        {out && (
          <>
            <h3 className="section-subtitle">{t('requests.out.recipient')}</h3>
            <div className="form-grid">
              {(
                [
                  ['name', 'requests.out.name', true],
                  ['phone', 'requests.out.phone', false],
                  ['address', 'requests.out.address', true],
                  ['city', 'requests.out.city', true],
                  ['postalCode', 'requests.out.postalCode', false],
                  ['country', 'requests.out.country', true],
                ] as const
              ).map(([field, label, required]) => (
                <div className="field-row" key={field}>
                  <label htmlFor={`rq-${field}`}>{t(label)}{required ? ' *' : ''}</label>
                  <input
                    id={`rq-${field}`}
                    value={shipTo[field] ?? ''}
                    aria-required={required}
                    onChange={(e) => setShipTo((s) => ({ ...s, [field]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
            <div className="field-row">
              <label htmlFor="rq-comment">{t('requests.out.comment')}</label>
              <textarea id="rq-comment" rows={2} maxLength={500} value={shipTo.comment ?? ''} onChange={(e) => setShipTo((s) => ({ ...s, comment: e.target.value }))} />
            </div>
          </>
        )}
      </div>

      <div className="card request-submit">
        {!canReview && <p className="sub">{t('requests.fillToContinue')}</p>}
        {error !== null && step === 'edit' && renderErr()}
        <div className="form-actions">
          <button type="submit" className="primary" disabled={!canReview || submitting}>
            {t('requests.review')}
          </button>
          {!revise && (
            <button type="button" disabled={!canSaveDraft || submitting} onClick={() => void saveDraft()}>
              {submitting ? t('requests.savingDraft') : t('requests.saveDraft')}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
